import { Link } from "react-router-dom";
import { Heart, Users, Lightbulb, HandHeart, MessagesSquare, MapPin, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ASSO } from "@/constants";

const VALEURS = [
  { icon: Heart, title: "Convivialité", text: "Des moments de partage qui renforcent les liens entre enfants, parents et équipes de l'école." },
  { icon: Users, title: "Solidarité", text: "Une communauté qui s'entraide et veille à ce qu'aucune famille ne reste à l'écart." },
  { icon: Lightbulb, title: "Éducation", text: "Des activités qui éveillent la curiosité et accompagnent les enfants dans leur parcours." },
  { icon: HandHeart, title: "Engagement", text: "Des bénévoles investis pour défendre les intérêts et les droits des enfants et des familles." },
];

const ENGAGEMENTS = [
  { icon: MessagesSquare, text: "Favoriser le dialogue autour de l'école" },
  { icon: Users, text: "Renforcer les liens entre les familles" },
  { icon: Lightbulb, text: "Encourager les initiatives collectives" },
  { icon: MapPin, text: "Participer à la vie locale et associative" },
  { icon: Sparkles, text: "Faire émerger des projets utiles et fédérateurs" },
  { icon: Heart, text: "Promouvoir l'écoute, le respect et la bienveillance" },
];

const PONTS = [
  "Entre l'école et les familles",
  "Entre les générations",
  "Entre les habitants",
  "Entre les initiatives locales",
];

const SYMBOLES = [
  { title: "L'arbre", text: "Symbole de croissance, d'enracinement et d'éducation. Il représente l'école comme un lieu d'apprentissage et d'épanouissement." },
  { title: "Le cœur", text: "Il incarne la bienveillance, l'entraide et la solidarité qui nous animent — il est au centre de notre engagement associatif." },
  { title: "Les feuilles colorées", text: "Elles évoquent les enfants, les familles et la diversité de nos parcours. Ensemble, elles forment une communauté riche et complémentaire." },
  { title: "Les couleurs pastel", text: "Vert, corail, jaune et lilas symbolisent l'écoute, la confiance, la convivialité et l'espoir d'un avenir harmonieux pour nos enfants." },
];

const EQUIPE = [
  { name: ASSO.president, role: "Président" },
  { name: "Le bureau", role: "Trésorerie, secrétariat et organisation" },
  { name: "Les bénévoles", role: "Animation et logistique des événements" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="bg-gradient-to-b from-sky-50 to-white py-12 sm:py-16 lg:py-20" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600">Qui sommes-nous</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-slate-900" data-testid="about-title">
              Une association au service des enfants et des familles de Toury
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-slate-600" data-testid="about-description">
              {ASSO.fullName} a pour objet d'organiser et concevoir des activités socio-éducatives
              et socio-culturelles, d'organiser des manifestations conviviales et solidaires auprès
              des enfants et des familles, et de veiller à la défense des intérêts moraux ainsi que
              des droits des enfants et des familles.
            </p>
          </div>
          <img
            src={ASSO.communityImage}
            alt="Parents et enfants lors d'une activité socio-éducative"
            className="rounded-3xl shadow-xl object-cover w-full aspect-[4/3]"
            data-testid="about-image"
          />
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-20" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-2">Nos valeurs</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Ce qui guide nos actions</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALEURS.map(({ icon: Icon, title, text }, i) => (
              <Card key={title} className="border-slate-200" data-testid={`value-card-${i}`}>
                <CardContent className="p-6 space-y-3">
                  <div className="h-11 w-11 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-emerald-700" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600">{text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-20 bg-white border-y border-slate-200" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <img
            src={ASSO.festiveImage}
            alt="Fête de l'école et événement solidaire en famille"
            className="rounded-3xl shadow-xl object-cover w-full aspect-[4/3] order-last lg:order-first"
            data-testid="team-image"
          />
          <div className="space-y-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600">L'équipe</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Le bureau et les bénévoles</h2>
            <p className="text-base leading-relaxed text-slate-600">
              L'association est portée par un bureau élu et une équipe de bénévoles investis.
              Chacun peut contribuer selon son temps et ses envies. Elle peut également porter
              et soutenir des listes de représentants de parents d'élèves lors des élections scolaires.
            </p>
            <ul className="space-y-4">
              {EQUIPE.map((m, i) => (
                <li key={i} className="flex items-start gap-4" data-testid={`team-member-${i}`}>
                  <div className="h-10 w-10 rounded-full bg-sky-100 flex items-center justify-center shrink-0 font-heading font-bold text-sky-700">
                    {m.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{m.name}</p>
                    <p className="text-sm text-slate-500">{m.role}</p>
                  </div>
                </li>
              ))}
            </ul>
            <Button className="bg-sky-700 hover:bg-sky-800" asChild data-testid="team-contact-btn">
              <Link to="/contact">Rejoindre l'équipe</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-emerald-50/70 to-white" data-testid="engagements-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-2">Nos engagements</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="engagements-title">Construire des ponts</h2>
            <p className="text-base text-slate-600 mt-3 leading-relaxed">
              Une école épanouie ne peut évoluer seule : elle grandit grâce aux liens tissés entre
              les familles, les acteurs éducatifs, les associations et l'ensemble des habitants.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ENGAGEMENTS.map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-4 bg-white rounded-2xl border border-slate-200 px-5 py-4 hover:shadow-md transition-shadow" data-testid={`engagement-${i}`}>
                <div className="h-10 w-10 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5 text-sky-700" />
                </div>
                <p className="text-sm font-medium text-slate-700">{text}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-white rounded-3xl border border-emerald-100 shadow-sm p-6 sm:p-8" data-testid="vision-block">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-4">Notre vision — construire des ponts</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {PONTS.map((p, i) => (
                <div key={i} className="flex items-center gap-2.5" data-testid={`pont-${i}`}>
                  <Heart className="h-4 w-4 text-pink-400 shrink-0" />
                  <p className="text-sm text-slate-700">{p}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-5 leading-relaxed">
              Parce qu'ensemble, nous pouvons créer un environnement plus favorable à l'épanouissement
              des enfants et au vivre-ensemble. CETF est un espace de dialogue, de proposition et
              d'action, ouvert à toutes les familles, attaché aux valeurs de solidarité, d'engagement
              citoyen et de coopération.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-20" data-testid="logo-meaning-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-2">Notre logo</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="logo-meaning-title">Une symbolique porteuse de sens</h2>
          </div>
          <div className="grid lg:grid-cols-[auto_1fr] gap-10 items-center">
            <div className="flex justify-center">
              <img src={ASSO.logoTreeUrl} alt="L'arbre du logo Cœur d'École Toury Famille" className="h-44 sm:h-56 w-auto object-contain" data-testid="logo-meaning-tree" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {SYMBOLES.map((s, i) => (
                <Card key={i} className="border-slate-200" data-testid={`symbole-${i}`}>
                  <CardContent className="p-6 space-y-2">
                    <h3 className="text-lg font-semibold text-slate-800">{s.title}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{s.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          <div className="mt-12 text-center" data-testid="about-closing-quote">
            <p className="font-serif text-xl sm:text-2xl text-slate-700 leading-relaxed max-w-3xl mx-auto">
              « CETF, c'est une équipe de parents bénévoles engagés, à vos côtés, pour faire grandir
              nos enfants dans un environnement bienveillant et solidaire. »
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
